import { useEffect, useState } from "react";
import { Loader2, Save, Settings2, Zap } from "lucide-react";
import { useConfigContable, useUpdateConfigContable } from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";
import type { ConfigContable } from "../types";

const CAMPOS: Array<{ key: keyof ConfigContable; label: string; hint: string }> = [
  { key: "cuenta_venta", label: "Cuenta de ventas", hint: "Ingresos por ventas (def. 700)" },
  { key: "cuenta_iva", label: "Cuenta IVA repercutido", hint: "IVA de ventas (def. 477)" },
  { key: "cuenta_cliente", label: "Cuenta de clientes", hint: "Deudores comerciales (def. 430)" },
  { key: "cuenta_devolucion", label: "Cuenta devoluciones", hint: "Devoluciones de venta (def. 708)" },
  { key: "cuenta_compra", label: "Cuenta de compras", hint: "Gastos por compras (def. 600)" },
  { key: "cuenta_proveedor", label: "Cuenta de proveedores", hint: "Acreedores comerciales (def. 400)" },
];

export function ConfigContablePage() {
  const { emisorId } = useAccountingStore();
  const { data: config, isLoading } = useConfigContable(emisorId);
  const updateMut = useUpdateConfigContable();

  const [form, setForm] = useState<Partial<ConfigContable>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    setSaved(false);
    await updateMut.mutateAsync({ emisorId, payload: form });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader
        title="Contabilidad"
        subtitle="Configuración contable del emisor."
        showEjercicio={false}
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {config && (
        <form onSubmit={save} className="max-w-3xl space-y-5">
          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
              <Settings2 size={16} className="text-brand-700" />
              <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                Cuentas por defecto
              </h2>
            </header>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {CAMPOS.map((c) => (
                <div key={c.key}>
                  <label className="text-xs font-black uppercase tracking-wider text-slate-500 block mb-1">
                    {c.label}
                  </label>
                  <input
                    type="text"
                    value={(form[c.key] as string) ?? ""}
                    onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-mono font-bold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none"
                  />
                  <p className="text-xs text-slate-400 mt-1">{c.hint}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
            <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-6 py-3 flex items-center gap-2">
              <Zap size={16} className="text-amber-600" />
              <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
                Automatización
              </h2>
            </header>
            <div className="p-6 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.auto_facturas ?? false}
                  onChange={(e) => setForm({ ...form, auto_facturas: e.target.checked })}
                  className="rounded border-slate-300 text-brand-700 w-4 h-4"
                />
                <div>
                  <span className="font-bold text-slate-800 text-sm">
                    Generar asiento automáticamente al emitir factura
                  </span>
                  <p className="text-xs text-slate-500">
                    Cuando se crea una factura, se contabiliza el asiento de venta.
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.auto_compras ?? false}
                  onChange={(e) => setForm({ ...form, auto_compras: e.target.checked })}
                  className="rounded border-slate-300 text-brand-700 w-4 h-4"
                />
                <div>
                  <span className="font-bold text-slate-800 text-sm">
                    Generar asiento automáticamente al registrar compra
                  </span>
                  <p className="text-xs text-slate-500">
                    Cuando se registra una compra, se contabiliza el asiento de gasto.
                  </p>
                </div>
              </label>
            </div>
          </section>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={updateMut.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 font-bold text-sm shadow-soft disabled:opacity-50"
            >
              {updateMut.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              Guardar configuración
            </button>
            {saved && (
              <span className="text-sm font-bold text-emerald-600">✓ Guardado correctamente</span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
